import {AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild,} from '@angular/core';
import {NgClass, NgForOf} from '@angular/common';
import {finalize, forkJoin, Subscription, take} from 'rxjs';
import {filter} from 'rxjs/operators';

import {DataTableComponent} from '../../../shared/components/data-table/data-table.component';
import {LoaderComponent} from '../../../shared/components/loader/loader.component';
import {DataTableColumnButton, TableColumn, TableLazyEvent} from '../../../shared/contracts/table-column.interface';

import {ConversationService} from '../../../conversations/services/conversation.service';
import {
  Conversation,
  ConversationPriority,
  ConversationStatus
} from '../../../conversations/contracts/conversation.interface';
import {WsMessage, WSMessageType} from '../../../conversations/contracts/message.interface';

import {UserService} from '../../../users/services/user.service';
import {User} from '../../../users/contracts/user.interface';

import {WebSocketService} from '../../../shared/services/webscoket.service';

import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';
import {ChatDialogComponent} from '../dialogs/chat-dialog/chat-dialog.component';
import {Button, ButtonSeverity} from 'primeng/button';
import {ConversationStatusComponent} from '../../../shared/components/conversation-status/conversation-status.component';
import {AuthService} from '../../../auth/services/auth.service';
import {FloatLabel} from 'primeng/floatlabel';
import {FormsModule} from '@angular/forms';
import {DatePicker} from 'primeng/datepicker';
import {Divider} from 'primeng/divider';
import {Select} from 'primeng/select';
import {ContactDisplayPipe} from '../../../shared/pipes/contact-display.pipe';
import {Contact} from '../../../contacts/contracts/contact.interface';
import {
  ConversationInitialsComponent
} from '../../../shared/components/conversation-initials/conversation-initials.component';

@Component({
  selector: 'app-assignment-list',
  standalone: true,
  imports: [DataTableComponent, LoaderComponent, Button, NgForOf, NgClass, FormsModule, DatePicker, Select, ConversationInitialsComponent, ConversationStatusComponent],
  providers: [DialogService, ContactDisplayPipe],
  templateUrl: './assignment-list.component.html',
  styleUrl: './assignment-list.component.scss',
})
export class AssignmentListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('statusTemplate') statusTemplate!: TemplateRef<any>;
  @ViewChild('mediaTemplate') mediaTemplate!: TemplateRef<any>;
  @ViewChild('avatarTemplate') avatarTemplate!: TemplateRef<any>;
  @ViewChild('priorityTemplate') priorityTemplate!: TemplateRef<any>;

  columns: TableColumn[] = [];
  data: Conversation[] = [];
  candidatesToAssign: User[] = [];
  candidatesToAssignWithNull: User[] = [];

  loading = false;
  selectedStatus: ConversationStatus | null = null;
  dataReady = false;
  viewReady = false;

  dateFrom?: Date | null = null;
  dateTo?: Date | null = null;
  selectedCandidate?: User;

  totalRecords = 0;
  rows = 20;
  lastLazyEvent: TableLazyEvent | undefined;

  priorities = [
    { label: 'Nessuna', value: ConversationPriority.DEFAULT, color: 'gray',   icon: 'fa-duotone fa-solid fa-signal-bars-slash' },
    { label: 'Bassa',   value: ConversationPriority.LOW,     color: 'blue',   icon: 'fa-duotone fa-solid fa-signal-bars-weak' },
    { label: 'Media',   value: ConversationPriority.MEDIUM,  color: 'orange', icon: 'fa-duotone fa-solid fa-signal-bars-fair' },
    { label: 'Alta',    value: ConversationPriority.HIGH,    color: 'red',    icon: 'fa-duotone fa-solid fa-signal-bars' },
  ];

  private subscription?: Subscription;
  private dialogRef?: DynamicDialogRef;

  constructor(
    private webSocketService: WebSocketService,
    private conversationService: ConversationService,
    private userService: UserService,
    private dialogService: DialogService,
    private authService: AuthService,
    private contactDisplayPipe: ContactDisplayPipe
  ) {}

  ngOnInit() {
    this.subscription = this.webSocketService.messages$
      .pipe(
        filter(msg =>
          msg.type === WSMessageType.CONVERSATION_CREATED ||
          msg.type === WSMessageType.CONVERSATION_UPDATED
        ),
        filter(msg => !msg.isErrorMessage)
      )
      .subscribe(msg => this.handleConversationEvent(msg));
  }

  ngAfterViewInit() {
    this.viewReady = true;
    this.prepareColumns();
  }

  getPriorityClass(priority: ConversationPriority) {
    switch (priority) {
      case ConversationPriority.DEFAULT:
        return 'priority-default';
      case ConversationPriority.LOW:
        return 'priority-low';
      case ConversationPriority.MEDIUM:
        return 'priority-medium';
      case ConversationPriority.HIGH:
        return 'priority-high';
      default:
        return '';
    }
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  onFilterChange() {
    this.loadAssignmentsList();
  }

  /** Load conversations + available users */
  loadAssignments(event: TableLazyEvent) {
    this.loading = true;
    this.lastLazyEvent = event;

    const isoDateFrom = this.dateFrom ? new Date(this.dateFrom).toISOString() : undefined;
    const isoDateTo = this.dateTo ? new Date(this.dateTo).toISOString() : undefined;

    forkJoin({
      result: this.conversationService.getAllToManage(
        this.selectedStatus,
        this.selectedCandidate?.id,
        isoDateFrom,
        isoDateTo,
        event.page,
        event.rows,
        event.filters
      ),
      users: this.userService.getAll(1, 1000),
    })
      .pipe(
        take(1),
        finalize(() => (this.loading = false))
      )
      .subscribe(({result, users}) => {
        this.data = result.data;
        this.totalRecords = result.total;
        this.candidatesToAssign = users.data;
        this.candidatesToAssignWithNull = [
          { id: undefined, email: 'Mostra Tutti' },
          ...this.candidatesToAssign
        ];
        this.dataReady = true;
        this.prepareColumns();
      });
  }

  loadAssignmentsList() {
    if (!this.lastLazyEvent) {
      this.lastLazyEvent = { page: 1, rows: this.rows, filters: [] };
    }

    this.loadAssignments(this.lastLazyEvent);
  }

  /**
   * Handles conversation.created and conversation.updated events for the management view.
   *
   * Supervisors see all statuses and always get in-place updates.
   * Self-assign operators only see TO_ASSIGN conversations: once a conversation is
   * picked up (ASSIGNMENT_PENDING or later), it leaves their pool and is removed.
   * Revoked conversations (returned to TO_ASSIGN) are re-inserted at the top.
   */
  private handleConversationEvent(wsMessage: WsMessage): void {
    const conversation: Conversation = wsMessage.content;
    const isSupervisor = this.authService.getCurrentUser()?.roleId === 2;
    const idx = this.data.findIndex(c => c.id === conversation.id);
    const exists = idx !== -1;

    if (wsMessage.type === WSMessageType.CONVERSATION_CREATED) {
      if (!exists) this.data.unshift(conversation);
      return;
    }

    if (isSupervisor) {
      exists ? (this.data[idx] = conversation) : this.data.unshift(conversation);
    } else {
      const currentUserId = this.authService.getCurrentUser()?.id;
      const isAssignedToMe = conversation.assignment?.assignedUser?.id === currentUserId;
      if (conversation.status === ConversationStatus.TO_ASSIGN || isAssignedToMe) {
        exists ? (this.data[idx] = conversation) : this.data.unshift(conversation);
      } else if (exists) {
        this.data.splice(idx, 1);
      }
    }
  }

  private showContactDisplay(conversation: Conversation, contact: Contact){
    return this.contactDisplayPipe.transform(contact, conversation.fromNumber)
  }

  /** Configure DataTable columns */
  private prepareColumns() {
    if (!this.dataReady || !this.viewReady) return;

    this.columns = [
      {customTemplate: this.priorityTemplate, type: 'template', header: 'Priorita'},
      {customTemplate: this.avatarTemplate, type: 'template', header: 'Avatar'},
      {field: 'creationDate', type: 'date', header: 'Data'},
      {type: 'text',
        header: 'Contatto',
        field: 'contact',
        transformer: this.showContactDisplay.bind(this),
        filterable: true,
      },
      {field: 'lastMessage', header: 'Messaggio', filterable: true},
      {
        field: 'status',
        type: 'template',
        customTemplate: this.statusTemplate,
        header: 'Stato',
      },
      {field: 'media', header: 'Media / Preventivi', customTemplate: this.mediaTemplate, type: 'template'},
      {
        field: 'assignment.assignedUser.id',
        type: 'dropdown',
        header: 'Operatore Associato',
        isDisabled: this.isAssignmentDisabled.bind(this),
        dropdown: {
          options: this.candidatesToAssign,
          label: 'Operatori',
          optionLabel: 'email',
          optionValue: 'id',
          onSelectDropdown: this.onAssignmentSelected.bind(this),
        },
      },
      {
        header: 'Azioni',
        buttons: <DataTableColumnButton[]>[
          {
            icon: 'pi-comments',
            tooltip: 'Chat',
            color: 'primary',
            click: this.openChatDialog.bind(this),
          },
          {
            icon: 'pi-undo',
            tooltip: 'Revoca',
            color: 'danger',
            disabledAsFunction: this.isRevokeDisabled.bind(this),
            click: this.revoke.bind(this),
          },
        ],
      },
    ];
  }

  /** Open chat */
  private openChatDialog(conversation: Conversation) {
    this.dialogRef = this.dialogService.open(ChatDialogComponent, {
      ...ChatDialogComponent.DIALOG_CONFIG,
      data: conversation,
    });
  }

  /** Assign conversation to user */
  private onAssignmentSelected(userId: number, conversation: Conversation) {
    if (!userId) return;

    this.loading = true;
    this.conversationService
      .assignConversation(conversation.id, userId)
      .pipe(
        take(1),
        finalize(() => (this.loading = false))
      )
      .subscribe();
  }

  /** Revoke assignment */
  revoke(conversation: Conversation) {
    this.conversationService
      .revokeAssignment(conversation.id)
      .pipe(take(1))
      .subscribe(() => (conversation.assignment = null));
  }

  /** Disable dropdown if not in "TO_ASSIGN" */
  private isAssignmentDisabled(conversation: Conversation) {
    return conversation.status !== ConversationStatus.TO_ASSIGN;
  }

  /** Disable revoke button if closed */
  private isRevokeDisabled(conversation: Conversation) {
    return conversation.status === ConversationStatus.CLOSED || conversation.status === ConversationStatus.TO_ASSIGN;
  }

  filters: { label: string; value: ConversationStatus | null; severity: ButtonSeverity; icon: string }[] =
    [
      {label: 'Tutte', value: null, severity: 'secondary', icon: 'fa-light fa-messages'},
      {label: 'Da Assegnare', value: ConversationStatus.TO_ASSIGN, severity: 'info', icon: 'fa-light fa-circle-plus'},
      {label: 'Assegnazione Pendente', value: ConversationStatus.ASSIGNED_PENDING, severity: 'warn', icon: 'fa-light fa-circle-pause'},
      {label: 'Aperte', value: ConversationStatus.OPEN, severity: 'success', icon: 'fa-light fa-circle-play'},
      {label: 'Chiuse', value: ConversationStatus.CLOSED, severity: 'danger', icon: 'fa-light fa-ban'},
    ];

  applyFilter(status: ConversationStatus) {
    this.selectedStatus = status;
    this.onFilterChange();
  }

  onPriorityChange(row: Conversation, priority: ConversationPriority) {
    console.log(row, priority);
    let conversation = row;
    row.priority = priority;
    this.conversationService.updatePriority(row.id, conversation).subscribe(data => console.log(data));
  }
}
